import { Button, Typography, spacing } from "@stepapp/ui";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { memo, useCallback, useState } from "react";

import { useCreatePaymentMethodMutation, useListPaymentMethodsQuery } from "@stepapp/core";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import ErrorModal from "../../Modal/ErrorModal";
import { CardForm } from "./CardForm.web";

type Props = {
  setLoading: (v: boolean) => void;
  onComplete: (paymentMethodId: string) => void;
  loading: boolean;
  showPaymentNotice?: boolean;
};

// For web use only
export const AddCard = memo(function AddCard({
  setLoading,
  onComplete,
  loading,
  showPaymentNotice = false,
}: Props) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { refetch } = useListPaymentMethodsQuery();
  const [createPaymentMethod] = useCreatePaymentMethodMutation();
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const createCardToken = useCallback(async () => {
    setLoading(true);

    const cardElement = elements?.getElement("card");
    if (!cardElement) return;

    const createTokenData = await stripe?.createToken(cardElement);

    if (createTokenData?.error || !createTokenData?.token.id) {
      setErrorMsg(createTokenData?.error?.message);
      return setLoading(false);
    }

    const { data, errors } = await createPaymentMethod({
      variables: { paymentMethodToken: createTokenData?.token.id },
    });

    if (data?.createPaymentMethod) {
      await refetch();
      onComplete(data?.createPaymentMethod || "");
    } else {
      setErrorMsg(errors?.[0]?.message);
    }

    setLoading(false);
  }, [createPaymentMethod, setLoading, onComplete, refetch, stripe, elements]);

  return (
    <>
      <CardForm />

      {showPaymentNotice && (
        <Typography type="secondary" variant="lightLabel" style={styles.paymentNotice}>
          {t("screens.serviceSummary.paymentNotice")}
        </Typography>
      )}

      <Button
        style={styles.addCardButton}
        disabled={loading}
        label={t("pages.serviceSummary.addCard")}
        loading={loading}
        onPress={createCardToken}
        type="primary"
      />

      <ErrorModal
        errorMsg={errorMsg}
        setErrorMsg={setErrorMsg}
        errorTitle={"pages.chooseCard.addCardFailed"}
        fallbackErrorMsg={t("pages.chooseCard.addCardFailedDescription")}
      />
    </>
  );
});

const styles = StyleSheet.create({
  addCardButton: {
    marginTop: spacing.unit * 3,
  },
  paymentNotice: {
    marginTop: spacing.unit * 2,
  },
});
